import React, { useCallback, useEffect } from 'react'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { type RootState } from '../../../store/store'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllCrossClientActionsQuery } from '../../../store/crossClient/crossClient'
import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import {
  resetCrossClientActions,
  selectCrossClientActionsHasInProgress,
  selectCrossClientActionsPollingEnabled,
  selectLastCrossClientActionUpdatedAt,
  selectLastCrossClientActionsManualTriggerAt,
  setCrossClientActions,
  stopCrossClientActionsPolling,
} from '../../../store/crossClient/crossClientActions'

const POLL_INTERVAL_MS = 3000
const MANUAL_TRIGGER_GRACE_MS = 30_000

export interface CrossClientActionsPollerProps {
  executionId: string
}

export const CrossClientActionsPoller: React.FC<CrossClientActionsPollerProps> = ({ executionId }) => {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()

  const accountId = useAppSelector(selectAccountId)
  const pollingEnabled = useAppSelector(selectCrossClientActionsPollingEnabled)

  const [triggerGetAllCrossClientActions] = useLazyGetAllCrossClientActionsQuery()

  useEffect(() => {
    dispatch(resetCrossClientActions())
  }, [dispatch, executionId])

  const pollActions = useCallback(async () => {
    try {
      const lastActionUpdatedAt = selectLastCrossClientActionUpdatedAt(store.getState())
      const result = await triggerGetAllCrossClientActions({ accountId, executionId, updatedAfter: lastActionUpdatedAt }).unwrap()
      dispatch(setCrossClientActions(result.data))

      const after = store.getState()
      const inProgress = selectCrossClientActionsHasInProgress(after)
      const lastTrigger = selectLastCrossClientActionsManualTriggerAt(after)
      const inGrace = lastTrigger !== null && Date.now() - lastTrigger < MANUAL_TRIGGER_GRACE_MS
      if (!inProgress && !inGrace) {
        dispatch(stopCrossClientActionsPolling())
      }
    } catch (e) {
      console.error(e)
      dispatch(stopCrossClientActionsPolling())
    }
  }, [dispatch, store, accountId, executionId, triggerGetAllCrossClientActions])

  const { start: startActionsPolling, stop: stopActionsPolling } = usePollingEffect(pollActions, [], { interval: POLL_INTERVAL_MS })

  useEffect(() => {
    if (pollingEnabled) {
      startActionsPolling()
    } else {
      stopActionsPolling()
    }
  }, [pollingEnabled, startActionsPolling, stopActionsPolling])

  return null
}
